import { isAxiosError } from 'axios';
import { apiClient } from './client';
import { ReceiptDocument, ReceiptGenerationPayload, ReceiptPreview } from '../types/payment';
import { PaginatedResponse } from '../types/pagination';
import {
  FinanceDashboardStats,
  Payment,
  PaymentMethod,
  PaymentStatus,
  PaymentType,
  StudentLedger,
} from '../types/payment';

/** Payment slip / receipt evidence upload — deliberately payments:create,
 * never payments:approve (see Payment/Document Checklist correction). */
type Progress = (percent: number) => void;
const progressConfig = (onProgress?: Progress) =>
  onProgress
    ? {
        onUploadProgress: (e: { loaded: number; total?: number }) =>
          onProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
      }
    : {};

export const uploadPaymentSlip = async (paymentId: number, file: File, onProgress?: Progress): Promise<Payment> => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post(`/payments/${paymentId}/slip`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    ...progressConfig(onProgress),
  });
  return data.data;
};

/** Phase 107 — the fields as multipart form data, with the receipt. */
const withReceipt = (fields: Record<string, unknown>, receipt: File) => {
  const formData = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    if (value !== undefined && value !== null && value !== '') formData.append(key, String(value));
  }
  formData.append('receipt', receipt);
  return formData;
};

/** Phase 107 — one uploaded receipt of a payment. */
export const downloadPaymentEvidence = async (
  paymentId: number,
  evidenceId: number,
): Promise<{ blob: Blob; filename: string }> => {
  const response = await apiClient.get(`/payments/${paymentId}/evidence/${evidenceId}`, { responseType: 'blob' });
  const disposition = (response.headers['content-disposition'] as string | undefined) ?? '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  return { blob: response.data, filename: match?.[1] ?? `payment-${paymentId}-receipt-${evidenceId}` };
};

/** Phase 107 — Finance asks for a replacement or additional receipt. */
export const requestPaymentEvidence = async (
  paymentId: number,
  body: { kind: 'REPLACEMENT' | 'ADDITIONAL'; reason: string },
): Promise<Payment> => {
  const { data } = await apiClient.post(`/payments/${paymentId}/evidence-request`, body);
  return data.data;
};

export const downloadPaymentSlip = async (paymentId: number): Promise<{ blob: Blob; filename: string }> => {
  const response = await apiClient.get(`/payments/${paymentId}/slip`, { responseType: 'blob' });
  const disposition = (response.headers['content-disposition'] as string | undefined) ?? '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  return { blob: response.data, filename: match?.[1] ?? `payment-${paymentId}-slip` };
};

export interface ListPaymentsParams {
  page: number;
  pageSize: number;
  studentId?: number;
  status?: PaymentStatus;
  type?: PaymentType;
}

export const listPayments = async (params: ListPaymentsParams): Promise<PaginatedResponse<Payment>> => {
  const { data } = await apiClient.get('/payments', { params });
  return data;
};

export interface CreatePaymentPayload {
  studentId: number;
  applicationId?: number;
  paymentCategoryId: number;
  type?: PaymentType;
  amount: number;
  currencyId: number;
  paymentMethod?: PaymentMethod;
  referenceNumber?: string;
  paymentDate?: string;
  notes?: string;
  /** Phase 104 — a rate other than the current automatic one (finance only). */
  exchangeRateId?: number;
}

export const createPayment = async (
  payload: CreatePaymentPayload,
  options: { receipt?: File | null; onProgress?: Progress } = {},
): Promise<Payment> => {
  const { data } = options.receipt
    ? await apiClient.post('/payments', withReceipt({ ...payload }, options.receipt), {
        headers: { 'Content-Type': 'multipart/form-data' },
        ...progressConfig(options.onProgress),
      })
    : await apiClient.post('/payments', payload);
  return data.data;
};

export const approvePayment = async (id: number): Promise<Payment> => {
  const { data } = await apiClient.post(`/payments/${id}/approve`);
  return data.data;
};

export const rejectPayment = async (id: number, reason: string): Promise<Payment> => {
  const { data } = await apiClient.post(`/payments/${id}/reject`, { reason });
  return data.data;
};

/** Reverse an approved payment (payments:reverse) — reason required; its
 * receipt is voided and it can be approved again later. */
export const reversePayment = async (id: number, reason: string): Promise<Payment> => {
  const { data } = await apiClient.post(`/payments/${id}/reverse`, { reason });
  return data.data;
};

/** Delete a pending or rejected payment, with a reason (2026-11-04) — kept
 * as DELETED with its history; approved payments are reversed instead. */
export const deletePayment = async (id: number, reason: string): Promise<Payment> => {
  const { data } = await apiClient.post(`/payments/${id}/delete`, { reason });
  return data.data;
};

/** The active SSCL setting, for the payment form's live breakdown. */
export const getPaymentSsclSetting = async (): Promise<{ percentage: number; calculationDivisor: number } | null> => {
  const { data } = await apiClient.get('/payments/sscl');
  return data.data;
};

/** Correct a rejected payment and send it back for finance approval —
 * creates a new pending payment linked to the rejected one (Phase 97). */
export const resubmitPayment = async (
  id: number,
  corrections: Partial<Omit<CreatePaymentPayload, 'studentId' | 'type'>>,
  options: { receipt?: File | null; onProgress?: Progress } = {},
): Promise<Payment> => {
  const { data } = options.receipt
    ? await apiClient.post(`/payments/${id}/resubmit`, withReceipt({ ...corrections }, options.receipt), {
        headers: { 'Content-Type': 'multipart/form-data' },
        ...progressConfig(options.onProgress),
      })
    : await apiClient.post(`/payments/${id}/resubmit`, corrections);
  return data.data;
};

export const getStudentPaymentSummary = async (
  studentId: number,
): Promise<Array<{ currencyId: number; total: number }>> => {
  const { data } = await apiClient.get(`/payments/students/${studentId}/summary`);
  return data.data;
};

export const getStudentLedger = async (studentId: number): Promise<StudentLedger> => {
  const { data } = await apiClient.get(`/payments/students/${studentId}/ledger`);
  return data.data;
};

export const getFinanceDashboard = async (): Promise<FinanceDashboardStats> => {
  const { data } = await apiClient.get('/payments/dashboard');
  return data.data;
};

/** Both GET /payments/:id/receipt and GET /payments/:id/invoice return a
 * real PDF (application/pdf) — must be fetched as a Blob, never as text.
 * `responseType: 'text'` forces axios to decode the binary buffer as a
 * UTF-8 string, corrupting it into exactly the garbled
 * "%PDF-1.4 obj stream ????" characters this fixes (Receipt PDF delivery
 * bug — the same fix applies to Invoice, which returns the same kind of
 * binary body). Error responses (e.g. 400/403) still arrive as a Blob
 * under this responseType — extractBlobErrorMessage re-reads that Blob as
 * JSON rather than ever opening an error body as if it were a PDF. */
/** Blob.prototype.text() isn't implemented everywhere this needs to run
 * (notably jsdom, used by this project's own frontend tests) — FileReader
 * is the one Blob-reading API available in every real browser and in jsdom. */
const readBlobAsText = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });

export const extractBlobErrorMessage = async (err: unknown, fallback: string): Promise<string> => {
  if (isAxiosError(err) && err.response?.data instanceof Blob) {
    try {
      const text = await readBlobAsText(err.response.data);
      const parsed = JSON.parse(text);
      if (typeof parsed?.message === 'string') return parsed.message;
    } catch {
      // Not JSON (or empty) — fall through to the generic message below.
    }
  }
  return isAxiosError(err) ? (err.response?.data?.message ?? fallback) : fallback;
};

// Exported so other PDF-backed resources (e.g. the student-level Invoice
// in api/students.ts) can reuse the same blob/error-handling plumbing
// instead of re-implementing it.
export const getPdfBlob = async (url: string, fallbackErrorMessage: string): Promise<Blob> => {
  try {
    const response = await apiClient.get(url, { responseType: 'blob' });
    return new Blob([response.data], { type: 'application/pdf' });
  } catch (err) {
    throw new Error(await extractBlobErrorMessage(err, fallbackErrorMessage));
  }
};

/** Forces a save-as of the same fetched PDF Blob via a client-side download
 * anchor (no second generator, matching the existing payment-slip download
 * pattern), instead of opening it inline. */
const downloadPdfBlob = async (url: string, filename: string, fallbackErrorMessage: string) => {
  try {
    const blob = await getPdfBlob(url, fallbackErrorMessage);
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  } catch (err) {
    window.alert(err instanceof Error ? err.message : fallbackErrorMessage);
  }
};

/** Document-preview blank-page investigation (2026-09-28) — these used to
 * open the fetched PDF Blob via `window.open(URL.createObjectURL(blob),
 * '_blank')`, the exact same broken pattern fixed for student documents:
 * Chromium reliably fails to navigate a *new* top-level browsing context
 * to a blob: URL created by a different one. Now returns the Blob so the
 * caller can hand it to the same shared <DocumentPreviewDialog> that
 * renders it inline in the current tab instead. */
/** The payment's receipt in force — or, with `receiptId`, a receipt voided
 * by a reversal (stamped VOID; finance reviewers only). */
const receiptUrl = (paymentId: number, receiptId?: number) =>
  `/payments/${paymentId}/receipt${receiptId ? `?receiptId=${receiptId}` : ''}`;

export const getReceiptBlob = (paymentId: number, receiptId?: number) => getPdfBlob(receiptUrl(paymentId, receiptId), 'Failed to load the receipt');

export const downloadReceipt = (paymentId: number, receiptNumber: string, receiptId?: number) =>
  downloadPdfBlob(receiptUrl(paymentId, receiptId), `receipt-${receiptNumber}.pdf`, 'Failed to download the receipt');

export const getInvoiceBlob = (paymentId: number) => getPdfBlob(`/payments/${paymentId}/invoice`, 'Failed to load the invoice');

export const downloadInvoice = (paymentId: number, invoiceNumber: string) =>
  downloadPdfBlob(`/payments/${paymentId}/invoice`, `invoice-${invoiceNumber}.pdf`, 'Failed to download the invoice');

/** Generate Receipt (Phase 104) — what a receipt in the chosen currency and
 * rate would show, with nothing saved. */
export const previewReceipt = async (paymentId: number, body: ReceiptGenerationPayload): Promise<ReceiptPreview> => {
  const { data } = await apiClient.post(`/payments/${paymentId}/receipt/preview`, body);
  return data.data;
};

/** Generate Receipt (Phase 104) — saves a new immutable receipt version. */
export const generateReceipt = async (paymentId: number, body: ReceiptGenerationPayload): Promise<ReceiptDocument> => {
  const { data } = await apiClient.post(`/payments/${paymentId}/receipt/documents`, body);
  return data.data;
};

const receiptDocumentUrl = (paymentId: number, documentId: number) =>
  `/payments/${paymentId}/receipt/documents/${documentId}`;

export const getReceiptDocumentBlob = (paymentId: number, documentId: number) =>
  getPdfBlob(receiptDocumentUrl(paymentId, documentId), 'Failed to load the receipt');

export const downloadReceiptDocument = (paymentId: number, documentId: number, fileName: string) =>
  downloadPdfBlob(receiptDocumentUrl(paymentId, documentId), fileName, 'Failed to download the receipt');
